'use client';

import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';

import {
  BucketsEnum,
  useEncryptedUploadFile,
  useUpdateUploadedFileStatus,
} from '@crehana/react-hooks/file-upload';

import { FileStorageStatusEnum } from '@/generated/graphql/types';
import type {
  TAttachmentFile,
  TCreateFile,
} from '@/shared/components/input-file/types';

export const useCreateFile = (originalId: number) => {
  const { uploadFile, uploading } = useEncryptedUploadFile(originalId);

  const [isErrorUploadFile, setIsErrorUploadFile] = useState<boolean>(false);
  const [newFileUpdated, setNewFileUpdated] = useState<TCreateFile>({
    uploadProgress: undefined,
  });

  const { updateFileStatus, error, loading } =
    useUpdateUploadedFileStatus(originalId);

  // biome-ignore lint/suspicious/useAwait: Ask @chris to review this
  const changeStatusFile = async (fileId: number) => {
    return updateFileStatus({
      file_id: fileId,
      new_status: FileStorageStatusEnum.StatusCompleted,
    });
  };

  const uploadFileToAws = async (file: File) => {
    let fileId: string | undefined = undefined;
    let fileUrl: string | undefined = undefined;
    try {
      setIsErrorUploadFile(false);
      const response = await uploadFile({
        file: file,
        options: {
          bucket: BucketsEnum.PublicDev,
          content_type: file.type,
          filename: file.name,
        },
        onUploadProgress: progress => {
          setNewFileUpdated(prev => {
            const updatedState = {
              ...prev,
              uploadProgress: progress,
            };
            return updatedState;
          });
        },
      });

      if (!response.success) {
        throw new Error('Upload failed');
      }

      setNewFileUpdated(prev => ({ ...prev, file: file }));

      const res = await changeStatusFile(Number(response.file.id));

      if (!res.success || error) {
        throw new Error('Upload in change state in function UploadFileToAws');
      }

      const urlFile =
        res.fileData.__typename === 'AnotherFile'
          ? res.fileData.relative_url
          : '';

      fileId = response.file.id;
      fileUrl = urlFile || '';
      setNewFileUpdated(prev => ({ ...prev, urlFile: fileUrl }));
    } catch (error) {
      console.error('Error to upload a file ', error);
      setIsErrorUploadFile(true);
      throw error;
    } finally {
      setNewFileUpdated(prev => ({ ...prev, progress: undefined }));
    }
    return { fileId, fileUrl };
  };

  const uploadMultipleFileToAws = async (
    file: File,
    keyFile: string,
    attachmentFileUpdate: Dispatch<
      SetStateAction<Record<string, TAttachmentFile>>
    >,
  ) => {
    try {
      const response = await uploadFile({
        file: file,
        options: {
          bucket: BucketsEnum.PublicDev,
          content_type: file.type,
          filename: file.name,
        },
        onUploadProgress: progress => {
          attachmentFileUpdate(prev => ({
            ...prev,
            [keyFile]: {
              ...prev[keyFile],
              progress: progress,
              isLoading: true,
            },
          }));
        },
      });
      if (!response.success) {
        throw new Error('Upload failed');
      }

      attachmentFileUpdate(prev => ({
        ...prev,
        [keyFile]: {
          ...prev[keyFile],
          fileId: Number(response.file.id),
        },
      }));

      const res = await changeStatusFile(Number(response.file.id));

      if (!res.success || error) {
        throw new Error(
          'Upload in change state in function uploadMultipleFileToAws',
        );
      }
    } catch (error) {
      console.error('Error to upload a file ', error);
      attachmentFileUpdate(prev => ({
        ...prev,
        [keyFile]: {
          ...prev[keyFile],
          isError: true,
          isLoading: false,
        },
      }));
    } finally {
      attachmentFileUpdate(prev => ({
        ...prev,
        [keyFile]: {
          ...prev[keyFile],
          isLoading: false,
          progress: undefined,
        },
      }));
    }
  };

  return {
    uploadFileToAws,
    uploadMultipleFileToAws,
    error: isErrorUploadFile || error,
    loading: uploading || loading,
    newFileUpdated,
  };
};
